/**
 * Leave Blocks Management Component
 * For Team Leaders and Admins to block leave dates
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Shield,
  Trash2,
  Plus,
  AlertCircle,
  X,
  Loader2
} from 'lucide-react';
import { formatCalendarDate, formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LeaveBlocks = ({ currentUser }) => {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    start_date: '',
    end_date: '',
    reason: '',
    scope: 'team',
    team_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [teams, setTeams] = useState([]);

  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'hr_manager';

  // TEAM-BLOCK-002: authorized teams — admin/HR get all teams, TLs only theirs
  useEffect(() => {
    axios.get(`${API}/api/leave/blocks/authorized-teams`)
      .then(res => setTeams((res.data?.teams || []).filter(t => t.is_active !== false)))
      .catch(() => setTeams([]));
  }, []);

  // TL with exactly one authorized team → preselect it (read-only display)
  useEffect(() => {
    if (!isAdmin && teams.length === 1) {
      setFormData(prev => (prev.team_id === teams[0].id ? prev : { ...prev, team_id: teams[0].id }));
    }
  }, [isAdmin, teams, showForm]);

  const fetchBlocks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/leave/blocks`);
      setBlocks(response.data.blocks || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching blocks:', err);
      setError('Failed to load leave blocks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlocks();
  }, [fetchBlocks]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      await axios.post(`${API}/api/leave/blocks`, formData);
      setShowForm(false);
      setFormData({ start_date: '', end_date: '', reason: '', scope: 'team', team_id: '' });
      fetchBlocks();
    } catch (err) {
      console.error('Error creating block:', err);
      alert(err.response?.data?.detail || 'Failed to create leave block');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (blockId) => {
    if (!window.confirm('Are you sure you want to remove this leave block?')) return;
    
    try {
      setDeleting(blockId);
      await axios.delete(`${API}/api/leave/blocks/${blockId}`);
      fetchBlocks();
    } catch (err) {
      console.error('Error deleting block:', err);
      alert(err.response?.data?.detail || 'Failed to delete leave block');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return formatDateOnly(dateStr);
  };

  const today = new Date().toISOString().split('T')[0];

  if (loading && blocks.length === 0) {
    return (
      <div className="space-y-4">
        {[1, 2].map(i => (
          <div key={i} className="bg-white rounded-lg p-4 animate-pulse border border-gray-100">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-3"></div>
            <div className="h-3 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="w-6 h-6 text-red-600" />
          <h3 className="text-lg font-semibold text-gray-900">Leave Blocks</h3>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Block Dates
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
          <div>
            <p className="text-sm text-yellow-800">
              <strong>Leave blocks</strong> prevent team members from applying leave for specific date ranges. 
              Use this during high workload periods or important deadlines.
            </p>
          </div>
        </div>
      </div>

      {/* Blocks List */}
      {blocks.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No active leave blocks</p>
          <p className="text-sm text-gray-500">Team members can freely apply for leave</p>
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map(block => (
            <div 
              key={block.id}
              className="bg-white rounded-lg border border-red-100 p-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      block.scope === 'all' 
                        ? 'bg-red-100 text-red-700' 
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {block.scope === 'all' ? 'All Teams' : (block.team_name || 'Team Only')}
                    </span>
                    <span className="text-sm text-gray-500">
                      by {block.blocked_by_name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-sm text-gray-700 mb-2">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span className="font-medium">
                      {formatCalendarDate(block.start_date)} — {formatCalendarDate(block.end_date)}
                    </span>
                  </div>
                  
                  <p className="text-sm text-gray-600 bg-red-50 rounded px-3 py-2">
                    <strong>Reason:</strong> {block.reason}
                  </p>
                </div>
                
                <button
                  onClick={() => handleDelete(block.id)}
                  disabled={deleting === block.id}
                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  title="Remove block"
                >
                  {deleting === block.id ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Trash2 className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Block Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Block Leave Dates</h3>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                    min={today}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                    min={formData.start_date || today}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Block
                </label>
                <textarea
                  value={formData.reason}
                  onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  required
                  minLength={5}
                  maxLength={200}
                  placeholder="e.g., High workload - end of quarter rush"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                />
                <p className="text-xs text-gray-500 mt-1">This message will be shown to users trying to apply leave.</p>
              </div>

              {isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Block Scope
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="scope"
                        value="team"
                        checked={formData.scope === 'team'}
                        onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                        className="text-red-600"
                        data-testid="block-scope-team"
                      />
                      <span className="text-sm">Specific Team</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="scope"
                        value="all"
                        checked={formData.scope === 'all'}
                        onChange={(e) => setFormData(prev => ({ ...prev, scope: e.target.value }))}
                        className="text-red-600"
                        data-testid="block-scope-all"
                      />
                      <span className="text-sm">All Teams (System-wide)</span>
                    </label>
                  </div>
                  {formData.scope === 'team' && (
                    <select
                      value={formData.team_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, team_id: e.target.value }))}
                      required
                      data-testid="block-team-select"
                      className="mt-3 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Select team…</option>
                      {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {!isAdmin && (
                <div data-testid="block-tl-team-section">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
                  {teams.length === 1 ? (
                    <div
                      className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-lg text-sm text-gray-700"
                      data-testid="block-tl-team-readonly"
                    >
                      {teams[0].name}
                    </div>
                  ) : teams.length > 1 ? (
                    <select
                      value={formData.team_id}
                      onChange={(e) => setFormData(prev => ({ ...prev, team_id: e.target.value }))}
                      required
                      data-testid="block-tl-team-select"
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    >
                      <option value="">Select your team…</option>
                      {teams.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-amber-600" data-testid="block-tl-no-team">
                      No team is linked to your profile — contact HR/Admin to set up your team before blocking dates.
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">The block applies only to members of this team.</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Shield className="w-4 h-4" />
                      Block Dates
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveBlocks;
